import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getApiUrl, fetchWithAuth } from '@/api'
import TimeSlotModal from './TimeSlotModal'

// ─── Types ───────────────────────────────────────────────────────────────────

interface CounselApplication {
  application_id: number
  member_name: string | null
  login_id: string | null
  phone: string | null
  affiliation_name: string | null
  scheduled_date: string
  start_time: string | null
  end_time: string | null
  status: number
  applied_at: string
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: '0', label: '확인대기' },
  { value: '1', label: '확인완료' },
  { value: '2', label: '신청취소' },
]

const STATUS_STYLE: Record<number, { label: string; cls: string }> = {
  0: { label: '확인대기', cls: 'bg-[#fff8e1] text-[#e67e22]' },
  1: { label: '확인완료', cls: 'bg-[#e8f5e9] text-[#388e3c]' },
  2: { label: '신청취소', cls: 'bg-[#f5f5f5] text-[#9e9e9e]' },
}

const LIMIT = 20


// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminCounselList() {
  const navigate = useNavigate()
  const [applications, setApplications] = useState<CounselApplication[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFilter, setDateFilter] = useState('')
  const [skip, setSkip] = useState(0)
  const [actionLoadingId, setActionLoadingId] = useState<number | null>(null)
  const [excelLoading, setExcelLoading] = useState(false)
  const [showSlotModal, setShowSlotModal] = useState(false)

  const fetchApplications = useCallback(async (status: string, date: string, skipVal: number) => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (status !== '') params.append('status', status)
      if (date !== '') params.append('scheduled_date', date)
      params.append('skip', String(skipVal))
      params.append('limit', String(LIMIT))
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/counseling/admin/applications?${params.toString()}`))
      if (!res.ok) throw new Error('조회 실패')
      const data = await res.json()
      setApplications(data)
      setTotal(data.length)
    } catch {
      alert('목록을 불러오는 데 실패했습니다.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchApplications(statusFilter, dateFilter, skip)
  }, [statusFilter, dateFilter, skip, fetchApplications])

  const handleApprove = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('신청을 확인완료 처리하시겠습니까?')) return
    setActionLoadingId(id)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/counseling/admin/applications/${id}/approve`), { method: 'POST' })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchApplications(statusFilter, dateFilter, skip)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleCancel = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    if (!window.confirm('신청을 취소 처리하시겠습니까?')) return
    setActionLoadingId(id)
    try {
      const res = await fetchWithAuth(getApiUrl(`/marubit/api/counseling/admin/applications/${id}/cancel`), { method: 'POST' })
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail ?? '처리 실패') }
      fetchApplications(statusFilter, dateFilter, skip)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : '처리에 실패했습니다.')
    } finally {
      setActionLoadingId(null)
    }
  }

  const handleExcelDownload = async () => {
    setExcelLoading(true)
    try {
      const params = new URLSearchParams()
      if (statusFilter !== '') params.append('status', statusFilter)
      const token = localStorage.getItem('access_token')
      const res = await fetch(getApiUrl(`/marubit/api/counseling/admin/applications/excel?${params.toString()}`), {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('다운로드 실패')
      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `counseling_applications_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.xlsx`
      a.click()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('엑셀 다운로드에 실패했습니다.')
    } finally {
      setExcelLoading(false)
    }
  }

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })

  const formatTime = (t: string | null) => (t ? t.slice(0, 5) : '')

  const totalPages = Math.ceil(total / LIMIT)
  const currentPage = Math.floor(skip / LIMIT) + 1

  return (
    <div className="bg-[#f5f2f0] min-h-screen p-8">
      <div className="max-w-[1200px] mx-auto">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-[22px] font-extrabold text-[#2a1f1a] mb-1">상담마루관리</h1>
            <p className="text-[13px] text-gray-400">
              {new Date().toLocaleDateString('ko-KR', {
                year: 'numeric', month: 'long', day: 'numeric', weekday: 'long',
              })}
            </p>
          </div>
          <button
            onClick={() => setShowSlotModal(true)}
            className="bg-white text-[#887267] text-[13px] font-semibold px-4 py-2 rounded-lg border border-[#e8d8ce] cursor-pointer hover:bg-[#f5f2f0] transition-colors"
          >
            ⏰ 상담시간 설정
          </button>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] px-5 py-4 mb-4 shadow-[0_2px_12px_rgba(136,114,103,0.06)] flex flex-wrap gap-3 items-end justify-between">
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">신청 상태</label>
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setSkip(0) }}
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] w-[150px] bg-white"
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-[#5a3d32] font-medium">상담 날짜</label>
              <input
                type="date"
                value={dateFilter}
                onChange={e => { setDateFilter(e.target.value); setSkip(0) }}
                className="border border-[#e8e0dc] rounded-lg px-3 py-2 text-[13px] text-[#2a1f1a] outline-none focus:border-[#887267] bg-white"
              />
            </div>
            {dateFilter && (
              <button
                onClick={() => { setDateFilter(''); setSkip(0) }}
                className="text-[12px] text-gray-400 hover:text-gray-600 border border-[#e8e0dc] rounded-lg px-3 py-2 bg-white cursor-pointer transition-colors h-[38px]"
              >초기화</button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[13px] text-gray-400">
              총 <span className="text-[#887267] font-bold">{total}</span>건
            </span>
            <button
              onClick={handleExcelDownload}
              disabled={excelLoading}
              className="bg-[#388e3c] text-white text-[13px] font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-[#2e7d32] transition-colors disabled:opacity-50 border-none"
            >
              {excelLoading ? '다운로드 중...' : '📥 엑셀로 저장'}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          {loading ? (
            <div className="min-h-[300px] flex items-center justify-center text-gray-300 text-[13px]">
              불러오는 중...
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">아이디</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">이름</th>
                  <th className="hidden sm:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[130px]">전화번호</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">소속</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">상담 날짜</th>
                  <th className="hidden md:table-cell text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[130px]">상담 시간</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[90px]">상태</th>
                  <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]"></th>
                </tr>
              </thead>
              <tbody>
                {applications.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-gray-300 text-[13px]">
                      신청 내역이 없습니다.
                    </td>
                  </tr>
                ) : applications.map(a => {
                  const st = STATUS_STYLE[a.status] ?? { label: String(a.status), cls: 'bg-gray-100 text-gray-500' }
                  const isLoading = actionLoadingId === a.application_id
                  const timeLabel = a.start_time && a.end_time
                    ? `${formatTime(a.start_time)} ~ ${formatTime(a.end_time)}`
                    : '-'
                  return (
                    <tr
                      key={a.application_id}
                      className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/counsel/${a.application_id}`)}
                    >
                      <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{a.login_id ?? '비회원'}</td>
                      <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{a.member_name ?? '-'}</td>
                      <td className="hidden sm:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{a.phone ?? '-'}</td>
                      <td className="hidden md:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{a.affiliation_name ?? '-'}</td>
                      <td className="px-4 py-3 text-[13px] text-[#2a1f1a] whitespace-nowrap">{formatDate(a.scheduled_date)}</td>
                      <td className="hidden md:table-cell px-4 py-3 text-[12px] text-gray-500 whitespace-nowrap">{timeLabel}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap" onClick={e => e.stopPropagation()}>
                        {a.status === 0 && (
                          <div className="flex gap-1.5">
                            <button
                              onClick={e => handleApprove(e, a.application_id)}
                              disabled={isLoading}
                              className="text-[12px] text-[#388e3c] hover:text-[#2e7d32] border border-[#c8e6c9] rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                            >확인</button>
                            <button
                              onClick={e => handleCancel(e, a.application_id)}
                              disabled={isLoading}
                              className="text-[12px] text-red-400 hover:text-red-600 border border-red-100 rounded px-2 py-1 bg-transparent cursor-pointer transition-colors disabled:opacity-50"
                            >취소</button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-1.5 mt-5">
            <button onClick={() => setSkip((currentPage - 2) * LIMIT)} disabled={currentPage === 1}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer">‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
              .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                if (idx > 0 && typeof arr[idx - 1] === 'number' && (p as number) - (arr[idx - 1] as number) > 1)
                  acc.push('...')
                acc.push(p)
                return acc
              }, [])
              .map((p, idx) =>
                p === '...'
                  ? <span key={`e-${idx}`} className="w-8 h-8 flex items-center justify-center text-[12px] text-gray-300">…</span>
                  : <button key={p} onClick={() => setSkip(((p as number) - 1) * LIMIT)}
                      className={`w-8 h-8 rounded-lg text-[12px] font-medium border transition-colors cursor-pointer ${currentPage === p ? 'bg-[#887267] text-white border-[#887267]' : 'bg-white text-[#5a3d32] border-[#e8e0dc] hover:bg-[#f5f2f0]'}`}>{p}</button>
              )}
            <button onClick={() => setSkip(currentPage * LIMIT)} disabled={currentPage === totalPages}
              className="w-8 h-8 rounded-lg text-[12px] border border-[#e8e0dc] bg-white text-[#887267] disabled:text-gray-300 disabled:cursor-not-allowed hover:bg-[#f5f2f0] cursor-pointer">›</button>
          </div>
        )}

      </div>

      {/* 상담시간 설정 모달 */}
      {showSlotModal && (
        <TimeSlotModal onClose={() => setShowSlotModal(false)} />
      )}
    </div>
  )
}

